import { useCallback, useEffect, useMemo, useState } from "react";

import { LOADING_ERROR, MISSING_PARAM_ERROR, Result, uiAmountToLamports } from "@bridgesplit/utils";
import {
    getZcCollateral,
    getZcLedger,
    useLoopscaleWalletContext,
    useDepositCollateralTransaction,
    AbfLoanExpanded,
    isVaultLpCollateral,
    getVaultLpStakeUnstakeIxParams,
    useStrategies,
    getAssetIdentifier,
    getAssetType,
    AssetTypeIdentifier,
    TokenListMetadataExpanded
} from "@bridgesplit/abf-react";
import { DepositCollateralParams } from "@bridgesplit/abf-sdk";
import { useTransactionSender } from "app/components/transactions";
import { TrackTransactionEvent } from "app/types";
import { trackSubmitTopup, usePermissionedLoanCheck } from "app/hooks";
import { useBalanceChecker } from "app/components/common";

import { UseAddCollateralReturn } from "./types";

export function useAddCollateral({
    loanExpanded,
    transactionOptions
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    transactionOptions?: { preventAutoDialogClose?: boolean };
}): UseAddCollateralReturn {
    const [inputAmount, setInputAmount] = useState<number | undefined>(undefined);
    const [selectedCollateralMint, setSelectedCollateralMint] = useState<string | undefined>(undefined);
    const ledgerAccount = getZcLedger(loanExpanded);
    const { data: strategies } = useStrategies(
        { addresses: ledgerAccount?.strategy ? [ledgerAccount.strategy] : [], pageSize: 1 },
        !ledgerAccount?.strategy
    );
    const strategy = strategies?.find((s) => s.strategy.address === ledgerAccount?.strategy);

    const supportedCollateralTokens = useMemo(() => {
        const tokensByMint = new Map<string, TokenListMetadataExpanded>();
        loanExpanded?.collateral.forEach((c) => {
            if (getAssetType(c.metadata) === AssetTypeIdentifier.SplToken)
                tokensByMint.set(c.metadata.mint, c.metadata);
        });
        strategy?.collateral.forEach((c) => {
            if (getAssetType(c.metadata) === AssetTypeIdentifier.SplToken)
                tokensByMint.set(c.metadata.mint, c.metadata);
        });
        return Array.from(tokensByMint.values());
    }, [loanExpanded?.collateral, strategy?.collateral]);

    useEffect(() => {
        if (!supportedCollateralTokens.length) return;
        const selectedIsSupported = supportedCollateralTokens.some((token) => token.mint === selectedCollateralMint);
        if (!selectedIsSupported) setSelectedCollateralMint(supportedCollateralTokens[0].mint);
    }, [selectedCollateralMint, supportedCollateralTokens]);

    const collateralMetadata = useMemo(
        () =>
            supportedCollateralTokens.find((token) => token.mint === selectedCollateralMint) ??
            supportedCollateralTokens[0],
        [selectedCollateralMint, supportedCollateralTokens]
    );
    const collateral = useMemo(
        () => loanExpanded?.collateral.find((c) => c.metadata.mint === collateralMetadata?.mint),
        [loanExpanded?.collateral, collateralMetadata?.mint]
    );

    // Balance checking for principal token
    const {
        balance,
        insufficientBalance,
        isLoading: balanceLoading
    } = useBalanceChecker({
        mint: collateralMetadata?.mint,
        amount: inputAmount,
        skip: false
    });

    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const send = useTransactionSender();
    const depositCollateral = useDepositCollateralTransaction();

    const { permissionDenied, permissionMessage } = usePermissionedLoanCheck(
        loanExpanded?.principalMetadata,
        collateralMetadata
    );

    const submit = useCallback(async () => {
        if (!loanExpanded || !activeWallet || !ledgerAccount) return Result.errFromMessage(LOADING_ERROR);

        if (!inputAmount || !collateralMetadata) return Result.errFromMessage(MISSING_PARAM_ERROR);
        const assetIdentifier = collateral?.assetIdentifier ?? getAssetIdentifier(collateralMetadata);
        if (!assetIdentifier) return Result.errFromMessage(MISSING_PARAM_ERROR);

        const params: DepositCollateralParams = {
            loan: loanExpanded.loan.address,
            depositMint: collateral?.assetMint ?? collateralMetadata.mint,
            decimals: collateralMetadata.decimals,
            amount: inputAmount,
            assetType: collateral?.assetType ?? getAssetType(collateralMetadata),
            assetIdentifier,
            expectedLoanValues: {
                expectedApy: ledgerAccount.apy,
                expectedLqt: ledgerAccount.lqtRatios
            },
            unstakeInstructions: getVaultLpStakeUnstakeIxParams({
                vault: collateral?.oracleAccount,
                amountLp: uiAmountToLamports(inputAmount ?? 0, collateralMetadata?.decimals ?? 0),
                skip: !isVaultLpCollateral(collateralMetadata)
            })
        };

        return await send(depositCollateral, params, {
            description: "Adding collateral",
            sendOptions: {
                onSuccess: () => {
                    setInputAmount(undefined); // Clear input after success
                }
            },
            mixpanelEvent: {
                key: TrackTransactionEvent.SubmitTopup,
                params: trackSubmitTopup(params, loanExpanded.principalMetadata, 0)
            },
            ...transactionOptions
        });
    }, [
        activeWallet,
        loanExpanded,
        ledgerAccount,
        depositCollateral,
        inputAmount,
        collateral,
        collateralMetadata,
        send,
        transactionOptions
    ]);

    const setMax = useCallback(() => {
        setInputAmount(balance);
    }, [balance]);

    return {
        // State
        inputAmount,
        setInputAmount,

        // Derived data
        collateral,
        collateralMetadata,
        supportedCollateralTokens,
        setCollateralToken: (token) => {
            setSelectedCollateralMint(token.mint);
            setInputAmount(undefined);
        },
        balance,
        insufficientBalance,
        balanceLoading,

        permissionDenied,
        permissionMessage,

        // Actions
        submit,
        setMax,

        // Props passed through
        loanExpanded
    };
}
